import { Helmet } from "react-helmet-async";
import { Outlet, Link, useLocation } from "react-router-dom";
import { PageBody } from "../components/PageBody";
import { Footer } from "../components/utils/Footer";
import { Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import {
	CpuChipIcon,
	Bars3Icon,
	MagnifyingGlassIcon,
	XMarkIcon,
	UserCircleIcon,
	PresentationChartLineIcon,
	ServerStackIcon,
	SparklesIcon,
	CurrencyDollarIcon,
	TableCellsIcon,
	Squares2X2Icon
} from '@heroicons/react/24/outline';
import { PoolIcon } from "../components/Icons";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { getLogoPeriodColor } from "../config/logoColors";
import { getThemeForPath } from "../config/theme";


export const NavBar = () => {
	// Reference for width tracking later
	// https://thewebdev.info/2021/05/24/how-to-get-the-width-of-an-element-in-a-react-component/
	// https://stackoverflow.com/questions/43817118/how-to-get-the-width-of-a-react-element
	return (
		<div className="flex flex-col min-h-screen justify-between">
			<Helmet>
				<title>VizBadger | Sports Analytics, Data-Driven Projects and Visualisation</title>
				<meta
					name="description"
					content="Discover, interact and explore with data visuals on sports analytics, simulations, data visualisation and sports betting."
				></meta>
			</Helmet>
			<nav className="bg-badger-off-white/50 border-b border-gray-200/30">
        <PageBody widthClass="max-w-screen-lg">
					<NavBarContent />
				</PageBody>
			</nav>
			<div className="mt-4 mx-4 mb-auto relative">
				<Outlet />
			</div>
			<Footer />
		</div>
	);
};


// Main navigation sections
const mainSections = [
  {
    label: 'Simulations',
    href: '/sports-models',
  },
  {
    label: 'Games',
    href: '/games',
  },
  {
    label: 'Betting',
    href: '/betting',
  },
  {
    label: 'Pool',
    href: '/pool/matches',
  },
];

// Featured projects matching Home.js - organized by headline theme
const projects = [
  {
    name: 'DuckDB Analytics Monitoring',
    description: 'Monitor and manage analytics infrastructure with DuckDB performance tracking and health checks.',
    href: 'duckdb',
    icon: ServerStackIcon,
    iconColour: 'text-badger-green',
  },
  {
    name: 'Designing Analytics Schemas',
    description: "Improve your analytics by designing effective relational database models and structures that answer business problems.",
    href: 'analytics/designing-analytics-schemas',
    icon: TableCellsIcon,
    iconColour: 'text-badger-green',
  },
  {
    name: 'Explore Test Match Batting Careers',
    description: 'Search and explore cricketers\' batting careers through visualisations and data analytics.',
    href: 'cricket',
    icon: MagnifyingGlassIcon,
    iconColour: 'text-badger-blue',
  },
  {
    name: 'Dynamic D3.js Graph',
    description: "Interact with a dynamic graph that automatically scales to device size with D3.js.",
    href: 'data-visualisation/dynamic-d3-components',
    icon: SparklesIcon,
    iconColour: 'text-badger-blue',
  },
  {
    name: 'Predictive Sports Modelling',
    description: 'Build and explore predictive models for sports outcomes using statistical methods and machine learning.',
    href: 'sports-models',
    icon: PresentationChartLineIcon,
    iconColour: 'text-badger-purple',
  },
  {
    name: 'English Pool Monte Carlo Simulation',
    description: 'Simulate and predict pool games using player shot accuracy and break efficiency. Adjust their skills and attributes to view the impact on results.',
    href: 'sports-models/english-pool-monte-carlo-simulation',
    icon: CpuChipIcon,
    iconColour: 'text-badger-purple',
  },
  {
    name: 'Sports Betting',
    description: 'Learn about sports betting strategies, value betting, and bankroll management techniques.',
    href: 'betting',
    icon: CurrencyDollarIcon,
    iconColour: 'text-badger-yellow',
  },
  {
    name: 'Amateur Pool Rankings',
    description: "Use the Colley ranking system to calculate which amateur pool player is the best.",
    href: 'pool/matches',
    icon: PoolIcon,
    iconColour: 'text-badger-red',
  },
  {
    name: 'Snakes and Ladders Simulation',
    description: "Play Snakes and Ladders or simulate thousands of games to analyze patterns and probabilities.",
    href: 'games/snakes-and-ladders',
    icon: Squares2X2Icon,
    iconColour: 'text-badger-purple',
  },
  {
    name: 'Nonogram Row Solver',
    description: "Work out which cells in a nonogram row must be filled or crossed off, with a step-by-step breakdown of the logic.",
    href: 'games/nonogram-solver',
    icon: Squares2X2Icon,
    iconColour: 'text-badger-purple',
  },
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
};


export const NavBarContent = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Get period color based on current route
  const periodColor = getLogoPeriodColor(location.pathname);
  
  // Check if a section is active
  const isSectionActive = (section) => {
    const normalizedPath = location.pathname.replace(/\/$/, '');
    return normalizedPath === section.href || normalizedPath.startsWith(section.href + '/');
  };
  
  // Get period color for a specific section
  const getSectionColor = (section) => {
    return getLogoPeriodColor(section.href);
  };
  
  // Get text color class for a section (for text color styling)
  const getSectionTextColor = (section) => {
    const themeColor = getThemeForPath(section.href);
    if (!themeColor) return null;
    // Convert bg-badger-* to text-badger-*
    return themeColor.replace('bg-', 'text-');
  };
  
  // Get hover color class - Tailwind requires explicit class names
  const getSectionHoverColor = (section) => {
    const themeColor = getThemeForPath(section.href);
    if (!themeColor) return null;
    
    // Map theme colors to hover text colors
    const colorMap = {
      'bg-badger-green': 'hover:text-badger-green',
      'bg-badger-blue': 'hover:text-badger-blue',
      'bg-badger-purple': 'hover:text-badger-purple',
      'bg-badger-yellow': 'hover:text-badger-yellow',
      'bg-badger-red': 'hover:text-badger-red',
      'bg-badger-orange': 'hover:text-badger-orange',
    };
    
    return colorMap[themeColor] || null;
  };

  return (
    <Popover className="relative">
      <div className="px-4 lg:px-0">
        {/* Mobile Layout */}
        <div className="flex flex-row justify-between items-center md:hidden py-3 h-16">
          <Link to="/" className="flex items-center">
            <Logo 
              periodColor={periodColor}
              size={28}
            />
          </Link>
          <div className="flex items-center">
            {isAuthenticated && (
              <Link to="/admin" className="mr-2">
                <UserCircleIcon className="h-6 w-6 text-gray-500 hover:text-badger-purple transition-colors" />
              </Link>
            )}
            <Popover.Button 
              className="px-2 py-1.5 rounded-md inline-flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-badger-purple transition-colors"
            >
              <span className="text-sm font-normal mr-1.5 hidden sm:block">Projects</span>
              <Bars3Icon className="h-5 w-5" aria-hidden="true" />
            </Popover.Button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center py-4">
          <div className="flex-none flex items-center">
            <Link to="/">
              <Logo 
                periodColor={periodColor}
                size={28}
              />
            </Link>
          </div>

          <div className="flex items-center gap-1">
            {/* Main section navigation links */}
            <nav className="hidden lg:flex items-center gap-1 mr-2">
              {mainSections.map((section) => {
                const active = isSectionActive(section);
                const textColorClass = getSectionTextColor(section);
                const hoverColorClass = getSectionHoverColor(section);
                
                return (
                  <Link
                    key={section.href}
                    to={section.href}
                    className={classNames(
                      "px-3 py-1.5 rounded-md text-sm transition-all duration-200",
                      active 
                        ? `font-semibold ${textColorClass || "text-gray-900"}`
                        : classNames(
                            "font-normal text-gray-600",
                            hoverColorClass || "hover:text-gray-900",
                            "hover:font-semibold"
                          )
                    )}
                  >
                    {section.label}
                  </Link>
                );
              })}
            </nav>
            
            <Popover.Group as="nav" className="flex items-center">
              <Popover className="relative">
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? 'text-badger-purple' : 'text-gray-600',
                        'group rounded-md inline-flex items-center justify-center hover:text-badger-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-badger-purple transition-colors',
                        'p-2 relative'
                      )}
                      aria-label="Projects menu"
                    >
                      <Bars3Icon
                        className={classNames(
                          open ? 'text-badger-purple' : 'text-gray-600',
                          'h-5 w-5 group-hover:text-badger-purple transition-colors'
                        )}
                        aria-hidden="true"
                      />
                      {/* Active state indicator */}
                      {projects.some(item => {
                        const normalizedPath = location.pathname.replace(/\/$/, '');
                        return normalizedPath === item.href || normalizedPath.startsWith(item.href + '/');
                      }) && (
                        <span 
                          className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                          style={{ backgroundColor: periodColor }}
                        />
                      )}
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute z-50 mt-3 right-0 w-screen max-w-xl sm:max-w-2xl md:max-w-3xl">
                        <div className="rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 overflow-hidden bg-white">
                          <div className="grid gap-6 px-5 py-4 sm:gap-6 sm:p-8 bg-white">
                            {projects.map((item) => {
                              const normalizedPath = location.pathname.replace(/\/$/, '');
                              const isActive = normalizedPath === item.href || normalizedPath.startsWith(item.href + '/');
                              
                              return (
                                <Popover.Button
                                  as={Link}
                                  key={item.name}
                                  to={item.href}
                                  className={classNames(
                                    "-m-2 p-2 flex items-start rounded-lg hover:bg-gray-50 transition-colors relative",
                                    isActive && "bg-gray-50"
                                  )}
                                >
                                  {item.name === "Amateur Pool Rankings" ? <item.icon classes={`flex-shrink-0 h-6 w-6 ${item.iconColour}`} aria-hidden="true" /> : <item.icon className={`flex-shrink-0 h-6 w-6 ${item.iconColour}`} aria-hidden="true" />}
                                  <div className="ml-4 flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className={classNames(
                                        "text-base font-medium",
                                        isActive ? "text-gray-900" : "text-gray-900"
                                      )}>{item.name}</p>
                                      {isActive && (
                                        <span 
                                          className="h-1.5 w-1.5 rounded-full"
                                          style={{ backgroundColor: periodColor }}
                                        />
                                      )}
                                    </div>
                                    <p className="mt-1/2 text-sm text-gray-500">{item.description}</p>
                                  </div>
                                </Popover.Button>
                              );
                            })}
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            </Popover.Group>

            {isAuthenticated && (
              <Link to="/admin" className="flex items-center text-gray-500 hover:text-badger-purple ml-3 transition-colors">
                <UserCircleIcon className="h-6 w-6" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu drawer */}
      <Transition
        as={Fragment}
        enter="transition ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Popover.Overlay className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm md:hidden" />
      </Transition>
      
      <Transition
        as={Fragment}
        enter="transition ease-out duration-300 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <Popover.Panel focus className="fixed top-0 right-0 bottom-0 z-50 w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto md:hidden">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <Popover.Button as={Link} to="/" className="flex items-center">
                    <Logo 
                      periodColor={periodColor}
                      size={28}
                    />
                  </Popover.Button>
                  <Popover.Button className="rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-badger-purple transition-colors">
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </Popover.Button>
                </div>
                
                {/* Navigation */}
                <nav className="flex-1 px-5 py-6 overflow-y-auto">
                  {/* Main Sections */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
                      Sections
                    </h3>
                    <div className="space-y-1">
                      {mainSections.map((section) => {
                        const active = isSectionActive(section);
                        const textColorClass = getSectionTextColor(section);
                        const hoverColorClass = getSectionHoverColor(section);
                        
                        return (
                          <Popover.Button
                            as={Link}
                            key={section.href}
                            to={section.href}
                            className={classNames(
                              "w-full p-3 flex items-center rounded-md transition-all duration-200",
                              active 
                                ? "bg-gray-50" 
                                : "hover:bg-gray-50"
                            )}
                          >
                            <span className={classNames(
                              "text-base flex-1",
                              active 
                                ? `font-semibold ${textColorClass || "text-gray-900"}`
                                : classNames(
                                    "font-normal text-gray-900",
                                    hoverColorClass || "",
                                    "hover:font-semibold"
                                  )
                            )}>
                              {section.label}
                            </span>
                            {active && (
                              <span 
                                className="h-2 w-2 rounded-full ml-2"
                                style={{ backgroundColor: getSectionColor(section) }}
                              />
                            )}
                          </Popover.Button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Projects */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
                      Projects
                    </h3>
                    <div className="space-y-1">
                      {projects.map((item) => {
                      const normalizedPath = location.pathname.replace(/\/$/, '');
                      const isActive = normalizedPath === item.href || normalizedPath.startsWith(item.href + '/');
                      
                      return (
                        <Popover.Button
                          as={Link}
                          key={item.name}
                          to={item.href}
                          className={classNames(
                            "w-full p-3 flex items-center rounded-md transition-colors",
                            isActive 
                              ? "bg-gray-50" 
                              : "hover:bg-gray-50"
                          )}
                        >
                          {item.name === "Amateur Pool Rankings" 
                            ? <item.icon classes={`flex-shrink-0 h-6 w-6 ${item.iconColour}`} aria-hidden="true" /> 
                            : <item.icon className={`flex-shrink-0 h-6 w-6 ${item.iconColour}`} aria-hidden="true" />
                          }
                          <div className="ml-3 flex-1 flex items-center justify-between">
                            <span className={classNames(
                              "text-base font-medium",
                              isActive ? "text-gray-900" : "text-gray-900"
                            )}>
                              {item.name}
                            </span>
                            {isActive && (
                              <span 
                                className="h-2 w-2 rounded-full ml-2"
                                style={{ backgroundColor: periodColor }}
                              />
                            )}
                          </div>
                        </Popover.Button>
                      );
                    })}
                    </div>
                  </div>
                </nav>
                
                {/* Footer (optional - can add auth link here if needed) */}
                {isAuthenticated && (
                  <div className="px-5 py-4 border-t border-gray-200">
                    <Popover.Button as={Link} to="/admin" className="w-full p-3 flex items-center rounded-md hover:bg-gray-50 transition-colors">
                      <UserCircleIcon className="h-6 w-6 text-gray-500" />
                      <span className="ml-3 text-base font-medium text-gray-900">Admin</span>
                    </Popover.Button>
                  </div>
                )}
              </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};
